"use client";

import { useSyncExternalStore } from "react";
import {
  hasBrowserPrivacySignal,
  isAudienceOptedOut,
  setAudienceOptOut,
  subscribeAudienceOptOut,
} from "@/lib/analytics/audience";

const COPY = {
  fr: {
    counted: "Votre navigateur est compté dans la mesure d'audience anonyme.",
    excluded: "Votre navigateur n'est pas compté dans la mesure d'audience anonyme.",
    signal:
      "Votre navigateur envoie un signal de refus du suivi (Global Privacy Control ou Do Not Track) : nous le respectons, vous n'êtes pas compté.",
    optOut: "Ne plus être compté",
    optIn: "Être compté à nouveau",
  },
  en: {
    counted: "Your browser is counted in the anonymous audience measurement.",
    excluded: "Your browser is not counted in the anonymous audience measurement.",
    signal:
      "Your browser sends a do-not-track signal (Global Privacy Control or Do Not Track): we honour it, you are not counted.",
    optOut: "Stop counting me",
    optIn: "Count me again",
  },
} as const;

/**
 * Lets a visitor object to the consent-free audience counter (CNIL exemption condition), from
 * the privacy policy. Renders the "counted" state on the server and the real one after hydration.
 *
 * @param locale - Language of the surrounding policy.
 */
export default function AudienceOptOut({ locale }: { locale: "fr" | "en" }) {
  const copy = COPY[locale];
  const optedOut = useSyncExternalStore(subscribeAudienceOptOut, isAudienceOptedOut, () => false);
  const signal = useSyncExternalStore(subscribeAudienceOptOut, hasBrowserPrivacySignal, () => false);

  if (signal) {
    return <p className="mt-4 text-sm text-slate-700">{copy.signal}</p>;
  }

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-slate-700" aria-live="polite">
        {optedOut ? copy.excluded : copy.counted}
      </p>
      <button
        type="button"
        onClick={() => setAudienceOptOut(!optedOut)}
        className="min-h-11 shrink-0 rounded-md px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition-colors hover:bg-slate-100 sm:min-h-9"
      >
        {optedOut ? copy.optIn : copy.optOut}
      </button>
    </div>
  );
}
